import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import { API_URL } from '@/common/config'
import { NO_INTERNET, UNAUTHENTICATED, SERVER_ERROR } from '@/common/messages'
import {
  AuthenticationError,
  BadRequestError,
  ValidationError,
  ForbiddenError,
  NoInternetError,
  NotFoundError,
  PointShortageError
} from '@/common/errors'

const ApiService = {
  init () {
    const service = this
    Vue.use(VueAxios, axios)
    Vue.axios.defaults.baseURL = API_URL
    // intercept every request
    Vue.axios.interceptors.request.use(
      function (request) {
        return request
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error)
      }
    )
    // intercept every response
    Vue.axios.interceptors.response.use(
      function (response) {
        return response
      }, function (error) {
        const exception = service.handleError(error.response)
        // check if config errorHandler is on
        return Promise.reject(exception)
      })
  },
  setHeader () {
    Vue.axios.defaults.withCredentials = true
    Vue.axios.defaults.headers.common[
      'Content-Type'
    ] = 'application/json'
  },
  setHeaderImage (type) {
    Vue.axios.defaults.headers.common[
      'Content-Type'
    ] = type
  },
  setResponseType (type) {
    Vue.axios.defaults.responseType = type
  },
  query (resource, params) {
    this.addParamsIsses(params)
    return Vue.axios.get(resource, params)
  },
  get (resource, id, params) {
    this.addParamsIsses(params)
    return Vue.axios.get(`${resource}/${id}`, params)
  },
  getCustomResource (resource) {
    return Vue.axios.get(resource)
  },
  post (resource, params) {
    resource = this.addResourceIsses(`${resource}`)
    return Vue.axios.post(resource, params)
  },
  update (resource, id, params) {
    resource = this.addResourceIsses(`${resource}/${id}`)
    return Vue.axios.put(resource, params)
  },
  updateResource (resource, params) {
    resource = this.addResourceIsses(resource)
    return Vue.axios.put(resource, params)
  },
  updateBulk (resource, params) {
    resource = this.addResourceIsses(`${resource}`)
    return Vue.axios.put(resource, params)
  },
  changePoint (resource, params) {
    resource = this.addResourceIsses(`${resource}`)
    return Vue.axios.put(resource, params)
  },
  changeStatus (resource) {
    resource = this.addResourceIsses(resource)
    return Vue.axios.put(resource)
  },
  delete (resource, id) {
    resource = this.addResourceIsses(`${resource}/${id}`)
    return Vue.axios.delete(resource)
  },
  deleteWithParams (resource, id, params) {
    resource = this.addResourceIsses(`${resource}/${id}`)
    return Vue.axios.delete(resource, { data: params })
  },
  customRequest (data) {
    return Vue.axios(data)
  },
  handleError (response, ignore = []) {
    if (ignore.length > 0 && ignore.includes(response.status)) return response
    let error = null
    if (!response) {
      error = new NoInternetError(NO_INTERNET)
    } else if (response.status === 400) {
      error = new BadRequestError(response.data.message, response.data.errors)
    } else if (response.status === 401) {
      error = new AuthenticationError(UNAUTHENTICATED)
    } else if (response.status === 402) {
      error = new PointShortageError(response.data.message)
    } else if (response.status === 403) {
      error = new ForbiddenError(response.data.message)
    } else if (response.status === 404) {
      error = new NotFoundError(response.data.message)
    } else if (response.status === 422 && response.config.url === 'login') {
      error = new ValidationError(response.data.errors.failure, null)
    } else if (response.status === 422) {
      error = new ValidationError('', response.data.errors)
    } else {
      error = new Error(SERVER_ERROR)
    }
    return error
  },
  addParamsIsses (params) {
    const urlParams = new URLSearchParams(document.location.search.substring(1))
    if ('params' in params === false) {
      params.params = {}
    }
    if ('isses' in params.params === false) {
      params.params.isses = urlParams.get('isses') || null
    }
  },
  addResourceIsses (resource) {
    const urlParams = new URLSearchParams(document.location.search.substring(1))
    const isses = urlParams.get('isses') || ''
    if (isses === '' || resource.indexOf('?isses=') !== -1 || resource.indexOf('&isses=') !== -1) {
      return resource
    } else if (resource.indexOf('?') !== -1) {
      return resource + `&isses=${isses}`
    } else {
      return resource + `?isses=${isses}`
    }
  }
}

export default ApiService
